import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, MenuItem, Select, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { translate as t } from 'hooks/useTranslations';
import type { EisFlagStatus } from '../../_types';
import { useEisFlag } from '../../_data_sources/useEisFlag';
import useUpdateEisFlag from '../../_data_sources/useUpdateEisFlag';
import PersonnelDetailsCard from './components/PersonnelDetailsCard';
import FlagSummaryCard from './components/FlagSummaryCard';
import FlagResolutionCard from './components/FlagResolutionCard';

const STATUS_OPTIONS: { key: EisFlagStatus; label: string }[] = [
  { key: 'Active', label: 'performanceReporting.eis.details.flaggedPersonnelDetails.status.active' },
  { key: 'Cleared', label: 'performanceReporting.eis.details.flaggedPersonnelDetails.status.cleared' },
  { key: 'Closed', label: 'performanceReporting.eis.details.flaggedPersonnelDetails.status.closed' },
];

const FlaggedPersonnelDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id, flagId, personnelId } = useParams<{ id: string; flagId: string; personnelId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const backUrl = `/admin/performance-reporting/early-intervention-system/details/${id}#tab=flaggedPersonnel`;

  const { data: eisFlag, isLoading: isFlagLoading } = useEisFlag(flagId ?? '');
  const { mutate: updateFlag, isLoading: isSaving } = useUpdateEisFlag();

  const [status, setStatus] = useState<EisFlagStatus>('Active');

  useEffect(() => {
    if (eisFlag?.status) setStatus(eisFlag.status);
  }, [eisFlag?.status]);
  const [resolutionValues, setResolutionValues] = useState<{
    disposition: string;
    correctiveActions: string[];
    comments: string;
  }>({ disposition: '', correctiveActions: [], comments: '' });

  const handleSave = () => {
    updateFlag(
      {
        flagId: flagId ?? '',
        data: {
          status,
          disposition: resolutionValues.disposition || undefined,
          correctiveActions: resolutionValues.correctiveActions,
          comments: resolutionValues.comments || undefined,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['eisFlaggedPersonnel', Number(id)]);
          navigate(backUrl);
        },
        onError: () => {
          enqueueSnackbar(
            t('performanceReporting.eis.details.flaggedPersonnelDetails.errors.saveFailed'),
            { variant: 'error' }
          );
        },
      }
    );
  };

  const contentHeaderRight = (
    <Stack
      direction='row'
      spacing={2}
      alignItems='center'
    >
      <Select
        value={status}
        size='small'
        displayEmpty
        onChange={(e) => setStatus(e.target.value as EisFlagStatus)}
      >
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.key}
            value={opt.key}
          >{`${t('performanceReporting.eis.details.flaggedPersonnelDetails.statusLabel')}: ${t(opt.label)}`}</MenuItem>
        ))}
      </Select>
      <Button
        variant='contained'
        size='small'
        loading={isSaving}
        loadingPosition='end'
        onClick={handleSave}
      >
        {t('performanceReporting.eis.details.flaggedPersonnelDetails.actions.save')}
      </Button>
    </Stack>
  );

  return (
    <TakeoverLayout
      pageTitle={t('performanceReporting.eis.details.flaggedPersonnelDetails.pageTitle')}
      backUrl={backUrl}
      contentHeaderRight={contentHeaderRight}
    >
      <Grid
        container
        spacing={3}
        alignItems='flex-start'
      >
        {/* Left column: Personnel details */}
        <Grid size={{ xs: 12, md: 3 }}>
          <PersonnelDetailsCard personnelId={personnelId ?? ''} />
        </Grid>

        {/* Right column: Flag summary + Flag resolution stacked */}
        <Grid size={{ xs: 12, md: 9 }}>
          <Stack spacing={3}>
            <FlagSummaryCard flagId={flagId ?? ''} />
            <FlagResolutionCard
              flagId={flagId ?? ''}
              isLoading={isFlagLoading}
              initialValues={{
                disposition: eisFlag?.disposition ?? '',
                correctiveActions: eisFlag?.correctiveActions ?? [],
                comments: eisFlag?.comments ?? '',
              }}
              onSave={setResolutionValues}
            />
          </Stack>
        </Grid>
      </Grid>
    </TakeoverLayout>
  );
};

export default FlaggedPersonnelDetails;
